import { isFeatureSupported } from '@app/adapters/silverfin'
import type {
  CheckPayload,
  ChecksCountPayload,
} from '@app/shared/types/sf-data'
import SFetch from '@app/utils/sfetch'
import { getAssistantChecksPath } from '../utils'

type toggleCheckEndpointType =
  | 'unread_ledger_assistant_checks_path'
  | 'read_ledger_assistant_checks_path'
  | 'unread_ledger_assistant_checks_v2_index_path'
  | 'read_ledger_assistant_checks_v2_index_path'

const getUrl = (type: 'read' | 'unread'): string => {
  const v2 = window.Silverfin.currentFirmFeatures.supports_v2_checks
  const route = `${type}_ledger_assistant_checks_${
    v2 ? 'v2_index_' : ''
  }path` as toggleCheckEndpointType

  return window.Silverfin.Routes[route](
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    window.Silverfin.currentLedgerId()
  )
}

const toggleChecks = ({
  type,
  checkIds,
}: {
  type: 'read' | 'unread'
  checkIds: string[]
}): Promise<void | null> => {
  const url = getUrl(type)

  return SFetch.json(`${url}`, {
    method: 'POST',
    body: { ids: checkIds },
  })
}

const toggleCheckDismissal = ({
  accountId,
  actionType,
  checkId,
  reconciliationId,
}: {
  accountId?: number
  actionType: 'undismiss' | 'dismiss'
  checkId: string
  reconciliationId?: number
}): Promise<void | null> => {
  const v2 = isFeatureSupported('supports_v2_checks')

  const route: keyof typeof window.Silverfin.Routes = `${actionType}_ledger_assistant_checks${
    v2 ? '_v2_index_' : '_'
  }path`

  const path = window.Silverfin.Routes[route](
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    window.Silverfin.currentLedgerId(),
    checkId,
    {
      account_id: accountId || 0,
      reconciliation_id: reconciliationId || 0,
    }
  )

  return SFetch.json(path, { method: 'POST' })
}

const getChecksByLedger = (
  options?: Record<string, unknown>
): Promise<CheckPayload | null> => {
  const url = getAssistantChecksPath(options)

  return SFetch.json<CheckPayload>(`${url}`)
}

const getUnreadChecksCount = (
  workflowId: number | null
): Promise<ChecksCountPayload | null> => {
  let url = ''
  if (workflowId) {
    url = window.Silverfin.Routes.unread_count_ledger_assistant_checks_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      window.Silverfin.currentLedgerId(),
      { skip_platform_checks: true, workflow_id: workflowId }
    )
  } else {
    url = window.Silverfin.Routes.unread_count_assistant_checks_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      {
        skip_platform_checks: true,
        ledger_id: window.Silverfin.currentLedgerId(),
      }
    )
  }

  return SFetch.json<ChecksCountPayload>(`${url}`)
}

export {
  toggleChecks,
  toggleCheckDismissal,
  getChecksByLedger,
  getUnreadChecksCount,
}
